<template>
  <b-modal
    v-model="visible"
    size="lg"
    scrollable
    centered
    hide-footer
    :title="t('journeyCoverTitle')"
    :dir="isHebrew ? 'rtl' : 'ltr'"
    modal-class="journey-cover-picker-modal"
  >
    <p class="cover-picker-hint">{{ t('journeyCoverHint') }}</p>

    <div :class="['cover-picker-grid', { 'is-busy': busy }]">
      <label class="cover-picker-tile cover-picker-upload">
        <input type="file" accept="image/*" :disabled="busy" @change="upload">
        <svg viewBox="0 0 24 24" width="30" height="30" aria-hidden="true">
          <path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
        </svg>
        <span v-if="uploading">{{ t('rideInfoUploading') }} {{ progress }}%</span>
        <span v-else>{{ t('journeyCoverUpload') }}</span>
      </label>

      <button
        v-for="option in options"
        :key="option.url"
        type="button"
        :title="option.label"
        :disabled="busy"
        :class="['cover-picker-tile', { 'is-picked': option.url === current }]"
        @click="choose(option.url)"
      >
        <img
          :src="$store.getters.getResizeImageForMobileDevices(option.url)"
          :alt="option.label"
          loading="lazy"
          @error="useFullImage($event, option.url)"
        >
        <span v-if="option.url === current" class="cover-picker-current">{{ t('journeyCoverCurrent') }}</span>
      </button>
    </div>

    <p v-if="failed" class="cover-picker-error" role="alert">{{ t('rideInfoUploadError') }}</p>
  </b-modal>
</template>

<script>
import firebase from 'firebase/app';
import 'firebase/storage';
import { mapActions } from "vuex";
import { translationMixin } from "@/mixins/translationMixin";
import { posterPhotoOptions } from "@/utils/posterStops";

/**
 * The ride manager's choice of the journey cover photo: any photo already in
 * the trip (its hotels and places, as the poster editor offers per stop), or a
 * new one uploaded where the ride settings form puts it. A choice is saved at
 * once as the ride's top page image, and the cover follows the ride header.
 */
export default {
  name: 'JourneyCoverPicker',
  mixins: [translationMixin],
  props: {
    ride: { type: Object, required: true },
    days: { type: Array, default: () => [] },
    // The photo the cover shows now: the saved one, else the first stop's.
    cover: { type: String, default: null },
  },
  data() {
    return { visible: false, uploading: false, saving: false, progress: 0, failed: false };
  },
  computed: {
    current() {
      return this.cover || null;
    },
    // The current photo first: it may be an upload no day uses.
    options() {
      const photos = posterPhotoOptions(this.days, 1, this.days.length)
        .filter(photo => photo.url !== this.current);
      if (this.current) photos.unshift({ url: this.current, label: '' });
      return photos;
    },
    busy() {
      return this.uploading || this.saving;
    },
  },
  methods: {
    ...mapActions(['saveRideTopPageImage']),
    open() {
      this.failed = false;
      this.visible = true;
    },
    useFullImage(event, url) {
      if (event.target.src !== url) event.target.src = url;
    },
    async choose(url) {
      if (this.busy) return;
      if (url === this.ride.top_page_image_url) {
        this.visible = false;
        return;
      }
      this.saving = true;
      this.failed = false;
      const saved = await this.saveRideTopPageImage({ rideId: this.ride.id, url });
      this.saving = false;
      if (saved) this.visible = false;
      else this.failed = true;
    },
    upload(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = '';
      if (!file || !file.type.startsWith('image/')) return;
      const time = new Date().toUTCString().split(', ')[1].split(' ').join('_');
      const task = firebase.storage().ref(`top_page_images/${this.ride.id}_${time}`).put(file);
      this.uploading = true;
      this.progress = 0;
      this.failed = false;
      task.on('state_changed',
        snapshot => {
          this.progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        },
        () => {
          this.uploading = false;
          this.failed = true;
        },
        async () => {
          let url = null;
          try {
            url = await task.snapshot.ref.getDownloadURL();
          } catch (error) {
            console.warn('cover upload failed:', error);
          }
          this.uploading = false;
          if (url) await this.choose(url);
          else this.failed = true;
        });
    },
  },
};
</script>

<style scoped>
/* Sizes in px: the app's root font is 10px, so rem here would be tiny. */
.cover-picker-hint {
  margin: 0 0 16px;
  font-size: 15px;
  color: #6e6e73;
}

.cover-picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
}

.cover-picker-grid.is-busy {
  opacity: 0.6;
  cursor: progress;
}

.cover-picker-tile {
  position: relative;
  aspect-ratio: 4 / 3;
  margin: 0;
  padding: 0;
  overflow: hidden;
  border: 0;
  border-radius: 16px;
  background: #eef1f5;
  box-shadow: 0 0 0 2px transparent;
  cursor: pointer;
  transition: box-shadow 0.2s ease;
}

.cover-picker-tile:hover {
  box-shadow: 0 0 0 2px var(--primary-color, #07a0c3);
}

.cover-picker-tile img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cover-picker-tile.is-picked {
  box-shadow: 0 0 0 4px var(--primary-color, #07a0c3);
}

.cover-picker-current {
  position: absolute;
  top: 8px;
  inset-inline-start: 8px;
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--primary-background, #07a0c3);
  color: var(--primary-text-color, #fff);
  font-size: 12px;
  font-weight: 700;
}

.cover-picker-upload {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 2px dashed var(--primary-color, #07a0c3);
  background: #fff;
  color: var(--primary-color, #07a0c3);
  font-size: 14px;
  font-weight: 700;
  text-align: center;
}

/* The input is the label's, so the whole tile opens the file picker. */
.cover-picker-upload input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.cover-picker-error {
  margin: 14px 0 0;
  font-size: 14px;
  font-weight: 600;
  color: #b42318;
}

@media (max-width: 575px) {
  .cover-picker-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>

<style>
/* The modal's own header (rendered outside this component's scope). */
.journey-cover-picker-modal .modal-content {
  border: 0;
  border-radius: 24px;
  overflow: hidden;
}

.journey-cover-picker-modal .modal-header {
  align-items: center;
  padding: 18px 22px;
  border-bottom: 1px solid #ececef;
}

.journey-cover-picker-modal .modal-title {
  font-size: 20px;
  font-weight: 800;
  color: #0d1117;
}

.journey-cover-picker-modal .modal-header .close {
  margin: 0;
  margin-inline-start: auto;
  padding: 0 4px;
  font-size: 30px;
  line-height: 1;
}

.journey-cover-picker-modal .modal-body {
  padding: 18px 22px 24px;
}
</style>
